import { svgLogoMediumSize, svgSparkles } from '@shared/assets/icons/svgs';
import { CircularProgress } from '@shared/components';
import { Text } from '@shared/components/text';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useAuthorizedIntegrationContext } from '@shared/contexts/pluginContexts/AuthorizedIntegrationContext';
import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { PromptContextProvider } from '@shared/contexts/PromptContext';
import { useAuthContext } from '@shared/contexts/useAuthContext';
import { JobType } from '@shared/generated/graphql';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { ActionRegistryContextProvider } from '@shared/plugin/contexts/ActionRegistryContext';
import { ConciergeContextProvider } from '@shared/plugin/contexts/ConciergeContext';
import { FeedContextProvider } from '@shared/plugin/contexts/FeedContext';
import { Feed } from '@shared/plugin/features/Feed';
import { FeedFilter } from '@shared/plugin/features/Feed/components/feed-filter/FeedFilter';
import { useBuildFromEmail } from '@shared/plugin/hooks/useBuildFromEmail';
import clsx from 'clsx';
import { useEffect } from 'react';
import { EmailThreadActivity } from './components/email-thread-activity';
import { NavigateToShipment } from './components/NavigateToShipment';
import { SharedInboxNotice } from './components/SharedInboxNotice';

const Home = () => {
  const { isAuthenticated } = useAuthContext();
  const {
    createOrder,
    createQuote,
    thisItem,
    hasUnconfirmedQuote,
    hasUnconfirmedOrder,
  } = useEmailThreadContext();
  const { me, loading } = useMeContext();
  const {
    authorized,
    loading: loadingAuthorizedIntegration,
    renderAuthInstructions,
  } = useAuthorizedIntegrationContext();
  const { sendToast } = useToast();
  const { page } = useAnalytics();
  const { buildFromEmail } = useBuildFromEmail();

  useEffect(() => {
    if (me?.id) {
      page('Home');
    }
  }, [me?.id]);

  useEffect(() => {
    if (createOrder.loading || createQuote.loading) return;

    const error = createOrder.error || createQuote.error;
    if (error) {
      sendToast(error.message, {
        variant: 'error',
        compact: true,
      });
    }
  }, [createOrder.error, createQuote.error]);

  const isLoggedIn = Boolean(me?.email && isAuthenticated);
  const showTransactions = authorized && !loading && isLoggedIn;

  const disableTransactions = Boolean(showTransactions && !thisItem?.id);
  const shouldAuthorize =
    !loading && isLoggedIn && !authorized && !loadingAuthorizedIntegration;

  const handleSendToVooma = async (jobType: JobType, buildHint?: string) => {
    await buildFromEmail(jobType, buildHint);
  };

  if (!thisItem) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="flex items-center">
          <span className="mx-1 inline-block fill-yellow-400">
            {svgSparkles}
          </span>
          <span className="mx-2 inline-block">{svgLogoMediumSize}</span>
          <span className="mx-1 inline-block fill-yellow-400">
            {svgSparkles}
          </span>
        </div>
        <Text className="p-4 text-gray-600" type="body-sm">
          {`Select an email to start the magic`}
        </Text>
      </div>
    );
  }

  return (
    <ConciergeContextProvider>
      <ActionRegistryContextProvider>
        <PromptContextProvider>
          <FeedContextProvider
            handleSendToVooma={handleSendToVooma}
            disableTransactions={disableTransactions}
            hasUnconfirmedQuote={hasUnconfirmedQuote}
            hasUnconfirmedOrder={hasUnconfirmedOrder}
          >
            <div className="flex h-full flex-col bg-gray-50">
              {!shouldAuthorize && (
                <div className={clsx('bg-white pb-2 drop-shadow')}>
                  <FeedFilter />
                </div>
              )}
              {!shouldAuthorize && <NavigateToShipment />}
              {!shouldAuthorize && <SharedInboxNotice />}
              <ul
                className="flex flex-col gap-y-[2px]"
                style={{ listStyle: 'none' }}
              >
                {loading && (
                  <div className="flex h-48 w-full flex-col items-center justify-center gap-4">
                    <CircularProgress size="small" />
                  </div>
                )}
                {shouldAuthorize && renderAuthInstructions()}
                <EmailThreadActivity />
              </ul>
              {showTransactions && <Feed />}
            </div>
          </FeedContextProvider>
        </PromptContextProvider>
      </ActionRegistryContextProvider>
    </ConciergeContextProvider>
  );
};

export default Home;
